import React, { createContext, useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  getLocalStorageItem,
  isTokenExpired,
  removeLocalStorageItem,
} from "../../../utils/HelperFunctions";
import fetchDataWithAuth from "../../../utils/api";
import { useNavigate, useLocation } from "react-router-dom";

const UserGroupsContext = createContext();

export const UserGroupsProvider = ({ children }) => {
  const [userGroups, setUserGroups] = useState([]);
  const [groupNames, setGroupNames] = useState([]);
  const [groupFunctions, setGroupFunctions] = useState([]);
  const [groupServices, setGroupServices] = useState([]);
  const [themes, setThemes] = useState([]);
  const [applications, setApplications] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const publicRoutes = ["/data-extraction/request"];
  const [groupNamesFetched, setGroupNamesFetched] = useState(false);

  const resetUserGroups = () => {
    console.log("resetting user groups");
    setUserGroups([]);
    setGroupNames([]);
    setGroupFunctions([]);
  };

  const fetchUserGroups = async (userID) => {
    try {
      const response = await fetchDataWithAuth(
        `/User/groups/user/${userID}`,
        "GET",
        navigate,
        t
      );
      setUserGroups(response);
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  };

  const fetchCommonGroup = async () => {
    try {
      let response = await fetch(
        `${process.env.REACT_APP_API_URL}/User/groupsList`, {
        method: "GET",
      }
      );
      if (!response.ok) {
        const errorText = await response.json();
        throw new Error(`${response.status}: ${errorText.message}`);
      }
      const result = await response.json();
      setUserGroups(result);
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  }

  useEffect(() => {
    const userID = getLocalStorageItem("UserId");
    const token = getLocalStorageItem("token");

    if (!userID || !token || isTokenExpired(token)) {
      // User is not logged in or the token is expired, avoid fetching user groups.
      //return;

      fetchCommonGroup();
    }
    if (publicRoutes.includes(location.pathname)) {

      /**do not fetch user groups for public pages */
      removeLocalStorageItem("token");
      removeLocalStorageItem("UserId");
      return;
    }
    if (userGroups.length === 0) {
      fetchUserGroups(userID);
    }
  }, []);

  useEffect(() => {

    if (typeof userGroups !== "undefined" && userGroups !== null) {
      if (userGroups.length > 0) {
        const groupNames = userGroups.map((userGroup) => {
          return userGroup.groupName;
        });
        setGroupNames(groupNames);
        setGroupNamesFetched(true);

        let userThemes = userGroups[0].userThemes;

        setThemes(userThemes);
        const userApplications = userGroups[0].userApplications;
        setApplications(userApplications);

        const _privilegedUserGroups = userGroups
          .filter((userGroup) => userGroup.groupName.toLowerCase() !== "common") // Filter out "common" groups

        const _CommonUserGroups = userGroups
          .filter((userGroup) => userGroup.groupName.toLowerCase() === "common") // Filter out "common" groups

        let privilegedFunctions = _privilegedUserGroups
          .flatMap((userGroup) => userGroup.groupFunctions.map((groupFunction) => groupFunction.functionName));

        let commonFunctions = _CommonUserGroups
          .flatMap((userGroup) => userGroup.groupFunctions.map((groupFunction) => groupFunction.functionName));

        const combinedFunctions = Array.from(
          new Set([...privilegedFunctions, ...commonFunctions])
        ).sort();

        setGroupFunctions(combinedFunctions);

        // userFunctions = Array.from(new Set(userFunctions)).sort();
        // setGroupFunctions(userFunctions);

        let userServices = _privilegedUserGroups
          .flatMap((userGroup) => userGroup.groupServices.map((groupService) => groupService));

        let commonServices = _CommonUserGroups
          .flatMap((userGroup) => userGroup.groupServices.map((groupService) => groupService));

        const combinedServices = [...userServices, ...commonServices];
        setGroupServices(combinedServices);
        //setGroupServices(userServices);

      }
    }
  }, [userGroups]);

  return (
    <UserGroupsContext.Provider
      value={{
        userGroups,
        setUserGroups,
        resetUserGroups,
        groupNames,
        themes,
        applications,
        groupFunctions,
        groupServices,
        groupNamesFetched
      }}
    >
      {children}
    </UserGroupsContext.Provider>
  );
};

export const useUserGroups = () => useContext(UserGroupsContext);
